import { FLAVOUR_ZONES, type IntensityPosition, type IntensityZone } from "../../types/intensity";

/**
 * A representative Agtron (SCA) reading for each position, one step darker
 * per step lighter roast — the inverse of the original roaster-facing
 * Agtron→position anchor table (positions 1–9 map to Agtron readings ~89.2
 * down to ~54.55). Where the original table had two anchors for the same
 * position (e.g. 51.8 and 57.3 both → 9), this uses their average; position 6
 * had no anchor at all in the original table, so it's the midpoint between
 * the neighbouring positions' (5 and 7) values instead.
 *
 * Unlike the forward direction, no interpolation is needed here — the input
 * is already a discrete 1–9 position, so this is a direct lookup.
 */
const AGTRON_ESTIMATE_BY_POSITION: Record<IntensityPosition, number> = {
  1: 89.2,
  2: 84.1,
  3: 80.8,
  4: 76.4,
  5: 70.4,
  6: 66.65,
  7: 62.9,
  8: 59.5,
  9: 54.55,
};

/**
 * The flavour zone a position falls in. The zone is always derived from the
 * final position, never entered or trusted from any other source — one
 * source of truth, so the two can't drift apart at a boundary.
 */
export const zoneFromPosition = (position: IntensityPosition): IntensityZone => {
  const match = (Object.keys(FLAVOUR_ZONES) as IntensityZone[]).find(
    (zone) => position >= FLAVOUR_ZONES[zone].min && position <= FLAVOUR_ZONES[zone].max
  );
  // Every position 1–9 is covered by exactly one zone; fall back to the
  // darkest band rather than widening the return type for an unreachable case.
  return match ?? "boldAndDark";
};

/**
 * Estimates the Agtron (SCA) reading behind a position, for sending to
 * Salesforce's `Agtron_Score__c` when the roaster reached that position via
 * the comparison dropdowns or the scale directly rather than typing a
 * reading themselves — a predicted number rather than a measured one, so
 * `Is_Agtron_Score_Provided__c` should read `false` alongside it.
 */
export const estimateAgtronFromIntensity = (position: IntensityPosition): number => AGTRON_ESTIMATE_BY_POSITION[position];
